import { zodResolver } from '@hookform/resolvers/zod';
import { Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { UserSelectControl } from '@/module/Nomen/User/UserSelectControl';
import { userOptions } from '@/module/Nomen/User/model/UserModel';

import { AsyncMultiSelectControl, Content, Form, FormSubmit } from '@/core/components';

import { UniversityConfigSchema } from './model/UniversityConfigSchema';

interface UniversityConfigFormProps extends FormSubmit<UniversityConfigSchema> {
  title: string;
}

export function UniversityConfigForm(props: UniversityConfigFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();

  const form = useForm<UniversityConfigSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  const { data, isLoading, isError: isUsersError } = useQuery(userOptions.getAll());
  // An account created through the API can carry no name yet, and an unlabelled option is unpickable.
  const userItems = useMemo(
    () =>
      (data ?? []).map(user => ({
        id: user.id,
        name: `${user.lastName ?? ''} ${user.firstName ?? ''}`.trim() || user.email,
      })),
    [data],
  );
  const usersNothingFound = (
    <Stack>
      <Text ta="center">{t('users.select.nothingFound')}</Text>
    </Stack>
  );

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        isDisabled={isUsersError}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <UserSelectControl
              name="researchViceRector"
              label={t('universityConfig.fields.researchViceRector')}
              isSubmitting={isSubmitting}
              clearable
              control={form.control}
            />
            <AsyncMultiSelectControl
              name="researchOfficeMembers"
              label={t('universityConfig.fields.researchOfficeMembers')}
              description={t('users.select.limit')}
              isSubmitting={isSubmitting}
              limit={100}
              options={userItems}
              isLoading={isLoading}
              isError={isUsersError}
              nothingFoundMessage={usersNothingFound}
              control={form.control}
            />
            <UserSelectControl
              name="internationalViceRector"
              label={t('universityConfig.fields.internationalViceRector')}
              isSubmitting={isSubmitting}
              clearable
              control={form.control}
            />
            <AsyncMultiSelectControl
              name="internationalOfficeMembers"
              label={t('universityConfig.fields.internationalOfficeMembers')}
              description={t('users.select.limit')}
              isSubmitting={isSubmitting}
              limit={100}
              options={userItems}
              isLoading={isLoading}
              isError={isUsersError}
              nothingFoundMessage={usersNothingFound}
              control={form.control}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
